import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Color from 'color';

import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';
import { IconContainer, OverflowMenuButton } from 'LegacyWeployDesignLanguage/icons';
import Fade from 'WeployDesignLanguage/transitions/Fade';
import Card from 'WeployDesignLanguage/Card';
import Menu, { MenuItem } from 'WeployDesignLanguage/Menu';
import Button from 'WeployDesignLanguage/buttons/Button';
import Typography from 'WeployDesignLanguage/Typography';

import { dateRangeToDuration, longDateWithTime } from '../../../utils/Date';
import SelectionCountdownTimer from './SelectionCountdownTimer';
import Avatars from './Avatars';

const MAXIMUM_NUMBER_OF_AVATARS = 5;

const StyledCard = styled(({ isClickable, ...rest }) => <Card {...rest} />)`
  text-align: center;
  flex-direction: column;
  position: relative;
  display: flex;
  justify-content: space-between;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.boxShadow.paper};
  margin: 0;

  ${props => props.highlighted && css`
    background-color: ${props.theme.colors.brand.primaryVariant1};
  `};
`;

const StyledHeading = styled.h6`
  margin-top: 0;
  margin-bottom: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledPositionsAndWeployees = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledPositionsAndLocation = styled.div`
  padding-right: ${props => props.theme.spacing[2]};
  text-align: left;
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const StyledTimeWrapper = styled.div`
  text-align: left;
  margin: ${props => props.theme.spacing[2]} 0 ${props => props.theme.spacing[1]};
`;

const StyledOwnerWrapper = styled.div`
  margin-top: ${props => props.theme.spacing[1]};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledJobDetailsWrapper = styled.div`
  padding: ${props => props.theme.spacing[1]};
  height: 100%;

  &:hover {
    cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
    background-color: ${({ isClickable, theme }) => (isClickable ? Color(theme.colors.text.primary).fade(0.90).string() : 'inherit')};

    ${props => props.highlighted && css`
      background-color: ${props.theme.colors.brand.primary};
    `};
  }
`;

const StyledPaddingWrapper = styled.div`
  padding: ${props => props.theme.spacing[1]};
`;

const StyledActions = styled.div`
  text-align: left;
  height: 2.75em;
`;

const StyledStatusMessages = styled.div`
  ${props => props.theme.typography.caption};
  color: ${props => props.theme.colors.semantic.warning};
  font-weight: 700;
  text-align: left;
`;

const StyledOverflowMenuWrapper = styled.div`
  position: absolute;
  right: 0.5rem;
  bottom: 0.25rem;
`;

const StyledButton = styled(Button)`
  align-self: flex-start;
  height: 100%;
`;

const StyledJobCardIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${props => props.theme.spacing[1]};
`;

const JobCard = ({
  job: {
    belongsToCurrentUser,
    city,
    expiryTime,
    finishTime,
    highlighted,
    id,
    isClickable,
    loaderVisible,
    owner,
    primaryButtonActionType,
    primaryButtonText,
    isOverflowMenuVisible: showOverflowMenu,
    startTime,
    state,
    status,
    statusMessages: messages,
    statusSubText,
    timerVisible,
    title,
  },
  numberOfWeployees,
  avatars,
  onCardClick,
  onExpiry,
  onPrimaryButtonClick,
  onTransferJobClick,
}) => {
  const [menuAnchorElement, setMenuAnchorElement] = React.useState(null);
  const open = Boolean(menuAnchorElement);
  const statusMessages = messages || [];

  const handleClick = (event) => {
    event.preventDefault();

    if (!isClickable) return;

    onCardClick(id);
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setMenuAnchorElement(event.currentTarget);
  };

  const handleMenuClose = () => (
    setMenuAnchorElement(null)
  );

  const handlePrimaryButtonClick = (event, jobId) => {
    event.stopPropagation();
    onPrimaryButtonClick(primaryButtonActionType, jobId);
  };

  return (
    <Fragment>
      <StyledCard highlighted={highlighted}>
        <StyledJobDetailsWrapper
          onClick={handleClick}
          isClickable={isClickable}
          highlighted={highlighted}
        >
          <StyledPaddingWrapper>
            <StyledHeading title={title}>{title}</StyledHeading>
            <StyledJobCardIconWrapper>
              <IconContainer />
            </StyledJobCardIconWrapper>
            { status && (
              <Typography variant="subtitle1" color="textPrimary" component="div">
                {status}
              </Typography>
            )}
            { timerVisible
              ? (
                <SelectionCountdownTimer
                  finishTime={expiryTime}
                  onExpiry={() => onExpiry(id)}
                />
              )
              : (
                <Typography variant="subtitle2" color="textSecondary" component="div">
                  {statusSubText}
                </Typography>
              )
            }
            { loaderVisible &&
              <Loader
                width="70%"
                loading
                type={Loader.LINEAR}
                text={null}
              />
            }
            <StyledTimeWrapper>
              <Typography variant="body1" color="textPrimary" component="div">
                { `${longDateWithTime(startTime)} -`}
              </Typography>
              <Typography variant="body1" color="textPrimary" component="div">
                { `${longDateWithTime(finishTime)} (${dateRangeToDuration(startTime, finishTime)})` }
              </Typography>
            </StyledTimeWrapper>
            <StyledPositionsAndWeployees>
              <StyledPositionsAndLocation>
                <Typography variant="subtitle1" color="textPrimary" component="div">
                  {`${numberOfWeployees} ${numberOfWeployees === 1 ? 'Weployee' : 'Weployees'}`}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  {`${city}, ${state}`}
                </Typography>
              </StyledPositionsAndLocation>
              { !!avatars.length &&
                <Avatars avatars={avatars} maxAvatars={MAXIMUM_NUMBER_OF_AVATARS} />
              }
            </StyledPositionsAndWeployees>
            {
              statusMessages.length > 0 &&
              (<StyledStatusMessages>{ statusMessages.map(message => <div key={message}>{ message }</div>) }</StyledStatusMessages>)
            }
            <StyledOwnerWrapper>
              <Typography variant="subtitle1" color="textPrimary">
                {belongsToCurrentUser ? 'Me' : owner}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Owner
              </Typography>
            </StyledOwnerWrapper>
          </StyledPaddingWrapper>
        </StyledJobDetailsWrapper>
        <StyledPaddingWrapper>
          <StyledActions>
            {
              primaryButtonText &&
                <StyledButton
                  appearance={Button.TEXT}
                  onClick={event => handlePrimaryButtonClick(event, id)}
                >
                  {primaryButtonText}
                </StyledButton>
            }
            {
              showOverflowMenu &&
                <StyledOverflowMenuWrapper>
                  <OverflowMenuButton onClick={handleMenuClick} />
                </StyledOverflowMenuWrapper>
            }
          </StyledActions>
        </StyledPaddingWrapper>
      </StyledCard>
      <Menu
        id="fade-menu"
        anchorEl={menuAnchorElement}
        keepMounted
        open={open}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
      >
        <MenuItem onClick={() => onTransferJobClick(id)}>Transfer Job</MenuItem>
      </Menu>
    </Fragment>
  );
};

JobCard.propTypes = {
  job: PropTypes.shape({
    belongsToCurrentUser: PropTypes.bool.isRequired,
    city: PropTypes.string,
    expiryTime: PropTypes.string,
    finishTime: PropTypes.string,
    highlighted: PropTypes.bool,
    isClickable: PropTypes.bool,
    id: PropTypes.string,
    loaderVisible: PropTypes.bool,
    owner: PropTypes.string.isRequired,
    primaryButtonActionType: PropTypes.string,
    primaryButtonText: PropTypes.string,
    isOverflowMenuVisible: PropTypes.bool.isRequired,
    startTime: PropTypes.string,
    state: PropTypes.string,
    status: PropTypes.string,
    statusMessages: PropTypes.array,
    title: PropTypes.string.isRequired,
    statusSubText: PropTypes.string,
    timerVisible: PropTypes.bool,
  }).isRequired,
  numberOfWeployees: PropTypes.number.isRequired,
  avatars: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
  })).isRequired,
  onCardClick: PropTypes.func,
  onExpiry: PropTypes.func,
  onPrimaryButtonClick: PropTypes.func,
  onTransferJobClick: PropTypes.func,
};

JobCard.defaultProps = {
  onPrimaryButtonClick: () => {},
  onExpiry: () => {},
  onTransferJobClick: () => {},
  onCardClick: () => {},
};

export default JobCard;
